// Active conversations store. Real content fills out in Session 2.5.

import { create } from 'zustand';
import type {
  AgentId,
  ChatAttachment,
  MessageCitation,
  MessageId,
  RichBlock,
  SessionId,
  SessionMedium,
  VisitorId,
} from '@prakash-software/shared';

export interface ConversationMessage {
  id: MessageId;
  sessionId: SessionId;
  /** Server commit order within this conversation. */
  sequence?: number;
  author: 'visitor' | 'agent' | 'ai' | 'system';
  authorName?: string;
  /** Authoring agent/visitor id when known. Used for stable "isMine"
   *  identification — name-matching breaks on hydration race or rename. */
  authorId?: AgentId | VisitorId;
  /** Server-resolved avatar URL for agent authors. Server returns a relative
   *  path; the client resolves to an absolute URL via resolveAvatarUrl(). */
  authorAvatarUrl?: string;
  text: string;
  /** Server-parsed markdown for `text` (AI replies only). When absent the
   *  renderer falls back to plain text + linkify. */
  richText?: RichBlock[];
  /** Answer buttons the visitor was offered (a flow's question). */
  quickReplies?: string[];
  sentAt: number;
  deliveryState?: 'sending' | 'delivered' | 'read';
  deliveredAt?: number;
  readAt?: number;
  attachments?: ChatAttachment[];
  /** AI replies: the knowledge sources the answer was grounded with. */
  citations?: MessageCitation[];
  /** Non-web channels only: where the reply is in delivery. */
  deliveryStatus?: 'queued' | 'sent' | 'delivered' | 'read' | 'failed';
  /** Why delivery failed, or why it is being retried. */
  deliveryError?: string;
}

export type NotesSaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';
export type InboxStatus = 'open' | 'pending' | 'resolved' | 'closed';
export type InboxPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface CallTranscriptEntry {
  callId: string;
  body: string;
  provider: string;
  language: string | null;
  createdAt: number;
}

export interface Conversation {
  aiPreview?: { id: string; text: string; revision: number } | null;
  sessionId: SessionId;
  /** Site the visitor's session belongs to. Optional until hydrateConversationDetail
   *  fills it in — agent surfaces (slash commands, site-config) that need it
   *  should gate on its presence. */
  siteId?: string;
  visitorId: VisitorId;
  visitorName: string;
  medium: SessionMedium;
  /** How the customer is reached. Absent/'web' = the website widget; 'api',
   *  'whatsapp', 'email' and 'instagram' are asynchronous channels with no live socket, no calls and
   *  no attachments. */
  channel?: 'web' | 'api' | 'whatsapp' | 'email' | 'instagram' | 'messenger';
  startedBy: 'visitor' | 'agent' | 'ai';
  startedAt: number;
  messages: ConversationMessage[];
  /** Agent-private notes per conversation. Persisted server-side. */
  notes: string;
  /** Save state for the notes textarea. */
  notesSaveState: NotesSaveState;
  /** Last server-confirmed save time, ms epoch. */
  notesUpdatedAt: number | null;
  /** AI-generated session summary. Persisted in `transcripts` table. */
  summary: string | null;
  summaryProvider: string | null;
  summaryGeneratedAt: number | null;
  /** Audio transcripts indexed by callId. */
  callTranscripts: Map<string, CallTranscriptEntry>;
  /** Only relevant when video=true under Option A. */
  inVideo: boolean;
  /**
   * Who is currently driving the conversation. Determines whether the AI
   * orchestrator will pick up the next visitor message ('ai') or stay quiet
   * because a human owns the seat ('agent').
   */
  ownedBy: 'agent' | 'ai';
  /** Owning agent id when ownedBy === 'agent'. */
  ownerAgentId?: AgentId;
  /**
   * Owning agent's display name + avatar, captured from the server so the
   * monitor + conversation header can identify who handled the chat even when
   * that agent is OFFLINE (live presence only covers online agents).
   */
  ownerAgentName?: string;
  ownerAgentAvatarUrl?: string;
  /** Shared agent-inbox triage metadata. */
  status?: InboxStatus;
  priority?: InboxPriority;
  tags?: string[];
  /** Department the conversation was routed (or moved) to. */
  departmentId?: string | null;
  departmentName?: string | null;
  /** Shared, per-conversation star (persisted server-side). */
  starred?: boolean;
}

interface ConversationsState {
  byId: Map<SessionId, Conversation>;
  activeId: SessionId | null;
  /** Sessions the agent has dismissed from the right-rail. They re-pin when
   *  a visitor or AI message arrives. */
  dismissed: Set<SessionId>;
  open: (c: Conversation) => void;
  setActive: (id: SessionId | null) => void;
  append: (id: SessionId, msg: ConversationMessage) => void;
  setAiPreview: (id: SessionId, preview: Conversation['aiPreview']) => void;
  markMessageReceipt: (
    id: SessionId,
    messageId: MessageId,
    status: 'delivered' | 'read',
    at: number,
  ) => void;
  /** A channel reply's delivery state changed (server push, or a manual retry). */
  setDeliveryStatus: (
    id: SessionId,
    messageId: MessageId,
    status: NonNullable<ConversationMessage['deliveryStatus']>,
    error?: string | null,
  ) => void;
  setNotes: (id: SessionId, notes: string) => void;
  setNotesSaveState: (id: SessionId, state: NotesSaveState, updatedAt?: number) => void;
  setSummary: (
    id: SessionId,
    summary: string,
    provider: string,
    generatedAt: number,
  ) => void;
  setCallTranscript: (id: SessionId, entry: CallTranscriptEntry) => void;
  hydrateConversationExtras: (
    id: SessionId,
    extras: {
      siteId?: string;
      notes?: { body: string; updatedAt: number | null };
      summary?: { summary: string; provider: string; generatedAt: number } | null;
      callTranscripts?: CallTranscriptEntry[];
    },
  ) => void;
  setVideo: (id: SessionId, inVideo: boolean) => void;
  setStarred: (id: SessionId, starred: boolean) => void;
  /** Update the header/display name after an authoritative identity change. */
  setVisitorName: (id: SessionId, visitorName: string) => void;
  close: (id: SessionId) => void;
  /** Hide from the active stack — does not delete the conversation. */
  dismiss: (id: SessionId) => void;
  /** Remove from `dismissed` so it shows again. */
  undismiss: (id: SessionId) => void;
  setOwner: (
    id: SessionId,
    ownedBy: Conversation['ownedBy'],
    ownerAgentId?: AgentId,
  ) => void;
  setInbox: (
    id: SessionId,
    patch: Partial<Pick<Conversation, 'status' | 'priority' | 'tags' | 'departmentId' | 'departmentName' | 'ownerAgentId' | 'ownerAgentName' | 'ownerAgentAvatarUrl'>>,
  ) => void;
  /**
   * Idempotent: create a Conversation if absent, else return the existing one.
   * Used by the bus bridge so a visitor's first message materializes a row.
   */
  ensure: (init: Conversation) => Conversation;
}

export const useConversationsStore = create<ConversationsState>((set, get) => ({
  byId: new Map(),
  activeId: null,
  dismissed: new Set(),
  open: (c) =>
    set((s) => {
      const next = new Map(s.byId);
      next.set(c.sessionId, c);
      return { byId: next, activeId: c.sessionId };
    }),
  setActive: (id) => set({ activeId: id }),
  setAiPreview: (id, preview) => set((s) => {
    const conversation = s.byId.get(id);
    if (!conversation || (preview && conversation.ownedBy !== 'ai')) return s;
    if (preview && (conversation.messages.some((m) => m.id === preview.id) || (conversation.aiPreview?.id === preview.id && conversation.aiPreview.revision >= preview.revision))) return s;
    const next = new Map(s.byId);
    next.set(id, { ...conversation, aiPreview: preview });
    return { byId: next };
  }),
  append: (id, msg) =>
    set((s) => {
      const existing = s.byId.get(id);
      if (!existing) return s;
      if (existing.messages.some((item) => item.id === msg.id)) return s;
      const next = new Map(s.byId);
      const messages = [...existing.messages, msg].sort((a, b) => {
        if (a.sequence !== undefined && b.sequence !== undefined) {
          return a.sequence - b.sequence;
        }
        return a.sentAt - b.sentAt;
      });
      next.set(id, { ...existing, messages, aiPreview: null });
      // Re-pin: incoming visitor/AI messages should bring the conversation
      // back to the active rail even if the agent had dismissed it.
      const repin = msg.author === 'visitor' || msg.author === 'ai';
      if (repin && s.dismissed.has(id)) {
        const nextDismissed = new Set(s.dismissed);
        nextDismissed.delete(id);
        return { byId: next, dismissed: nextDismissed };
      }
      return { byId: next };
    }),
  markMessageReceipt: (id, messageId, status, at) =>
    set((s) => {
      const existing = s.byId.get(id);
      if (!existing) return s;
      const index = existing.messages.findIndex((message) => message.id === messageId);
      if (index < 0) return s;
      const current = existing.messages[index]!;
      if (current.deliveryState === 'read' || current.deliveryState === status) return s;
      const messages = [...existing.messages];
      messages[index] = {
        ...current,
        deliveryState: status,
        deliveredAt: current.deliveredAt ?? at,
        ...(status === 'read' ? { readAt: current.readAt ?? at } : {}),
      };
      const next = new Map(s.byId);
      next.set(id, { ...existing, messages });
      return { byId: next };
    }),
  setDeliveryStatus: (id, messageId, status, error) =>
    set((s) => {
      const existing = s.byId.get(id);
      if (!existing) return s;
      const index = existing.messages.findIndex((message) => message.id === messageId);
      if (index < 0) return s;
      const current = existing.messages[index]!;
      const { deliveryError: _drop, ...rest } = current;
      const messages = [...existing.messages];
      messages[index] = { ...rest, deliveryStatus: status, ...(error ? { deliveryError: error } : {}) };
      const next = new Map(s.byId);
      next.set(id, { ...existing, messages });
      return { byId: next };
    }),
  setNotes: (id, notes) =>
    set((s) => {
      const existing = s.byId.get(id);
      if (!existing) return s;
      const next = new Map(s.byId);
      // Typing flips state to 'dirty'; the save flow transitions it to
      // 'saving' → 'saved' / 'error'. We only flip when the body actually
      // changed so programmatic seeds (hydration) don't mark clean state
      // as dirty.
      const changed = existing.notes !== notes;
      next.set(id, {
        ...existing,
        notes,
        notesSaveState: changed ? 'dirty' : existing.notesSaveState,
      });
      return { byId: next };
    }),
  setNotesSaveState: (id, state, updatedAt) =>
    set((s) => {
      const existing = s.byId.get(id);
      if (!existing) return s;
      const next = new Map(s.byId);
      next.set(id, {
        ...existing,
        notesSaveState: state,
        notesUpdatedAt:
          state === 'saved' && typeof updatedAt === 'number'
            ? updatedAt
            : existing.notesUpdatedAt,
      });
      return { byId: next };
    }),
  setSummary: (id, summary, provider, generatedAt) =>
    set((s) => {
      const existing = s.byId.get(id);
      if (!existing) return s;
      const next = new Map(s.byId);
      next.set(id, {
        ...existing,
        summary,
        summaryProvider: provider,
        summaryGeneratedAt: generatedAt,
      });
      return { byId: next };
    }),
  setCallTranscript: (id, entry) =>
    set((s) => {
      const existing = s.byId.get(id);
      if (!existing) return s;
      const transcripts = new Map(existing.callTranscripts);
      transcripts.set(entry.callId, entry);
      const next = new Map(s.byId);
      next.set(id, { ...existing, callTranscripts: transcripts });
      return { byId: next };
    }),
  hydrateConversationExtras: (id, extras) =>
    set((s) => {
      const existing = s.byId.get(id);
      if (!existing) return s;
      const next = new Map(s.byId);
      const patch: Partial<Conversation> = {};
      if (extras.siteId !== undefined) patch.siteId = extras.siteId;
      // An unsaved local draft wins over the server copy: a background reload
      // (e.g. the reconnect resync) can land while the agent is typing.
      const draftPending = existing.notesSaveState === 'dirty' || existing.notesSaveState === 'saving';
      if (extras.notes && !draftPending) {
        patch.notes = extras.notes.body;
        patch.notesUpdatedAt = extras.notes.updatedAt;
        patch.notesSaveState = 'idle';
      }
      if (extras.summary === null) {
        patch.summary = null;
        patch.summaryProvider = null;
        patch.summaryGeneratedAt = null;
      } else if (extras.summary) {
        patch.summary = extras.summary.summary;
        patch.summaryProvider = extras.summary.provider;
        patch.summaryGeneratedAt = extras.summary.generatedAt;
      }
      if (extras.callTranscripts) {
        const m = new Map<string, CallTranscriptEntry>();
        for (const t of extras.callTranscripts) m.set(t.callId, t);
        patch.callTranscripts = m;
      }
      next.set(id, { ...existing, ...patch });
      return { byId: next };
    }),
  setVideo: (id, inVideo) =>
    set((s) => {
      const existing = s.byId.get(id);
      if (!existing) return s;
      const next = new Map(s.byId);
      next.set(id, { ...existing, inVideo });
      return { byId: next };
    }),
  setStarred: (id, starred) =>
    set((s) => {
      const existing = s.byId.get(id);
      if (!existing) return s;
      const next = new Map(s.byId);
      next.set(id, { ...existing, starred });
      return { byId: next };
    }),
  setVisitorName: (id, visitorName) =>
    set((s) => {
      const existing = s.byId.get(id);
      if (!existing || existing.visitorName === visitorName) return s;
      const next = new Map(s.byId);
      next.set(id, { ...existing, visitorName });
      return { byId: next };
    }),
  close: (id) =>
    set((s) => {
      const next = new Map(s.byId);
      next.delete(id);
      const nextDismissed = new Set(s.dismissed);
      nextDismissed.delete(id);
      return {
        byId: next,
        dismissed: nextDismissed,
        activeId: s.activeId === id ? null : s.activeId,
      };
    }),
  dismiss: (id) =>
    set((s) => {
      const next = new Set(s.dismissed);
      next.add(id);
      return { dismissed: next };
    }),
  undismiss: (id) =>
    set((s) => {
      if (!s.dismissed.has(id)) return s;
      const next = new Set(s.dismissed);
      next.delete(id);
      return { dismissed: next };
    }),
  setOwner: (id, ownedBy, ownerAgentId) =>
    set((s) => {
      const existing = s.byId.get(id);
      if (!existing) return s;
      const next = new Map(s.byId);
      next.set(id, { ...existing, ownedBy, ownerAgentId, ...(ownedBy !== 'ai' ? { aiPreview: null } : {}) });
      return { byId: next };
    }),
  setInbox: (id, patch) =>
    set((s) => {
      const existing = s.byId.get(id);
      if (!existing) return s;
      const next = new Map(s.byId);
      next.set(id, { ...existing, ...patch });
      return { byId: next };
    }),
  ensure: (init) => {
    const existing = get().byId.get(init.sessionId);
    if (existing) return existing;
    get().open(init);
    return init;
  },
}));

// Note: do NOT export a `selectAll` that returns `Array.from(...)` —
// Zustand v5 compares with Object.is so a fresh array each call triggers
// an infinite render loop. Read `s.byId` (stable reference) and derive
// arrays in a `useMemo` at the call site.
export const selectActive = (s: ConversationsState): Conversation | null =>
  s.activeId ? s.byId.get(s.activeId) ?? null : null;

// Helpers used by the dev panel + Session 2.5
// Phase 5: sessions.id and messages.id are UUID-typed in Postgres, so the
// counter-based IDs from earlier phases would fail the insert and the event
// would never fan out. We mint UUIDs everywhere now.
function uuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}
export function newSessionId(): SessionId {
  return uuid() as SessionId;
}
export function newMessageId(): MessageId {
  return uuid() as MessageId;
}
export function asAgentId(v: string): AgentId {
  return v as AgentId;
}
